/**
 * Credits Service — wraps the grant_credits + spend_credits + get_user_balance RPCs.
 *
 * The Supabase client is injected via factory so both consumer apps
 * (jubit-ai-universe, jubuddy-game) share this code without coupling to
 * a specific client instance.
 *
 * Exposes:
 *   - `createCreditsService(supabase)` returning bound `grantCredits`,
 *     `spendCredits`, `getUserBalance`.
 *
 * The grant RPC is idempotent for share_bonus (unique index enforces one
 * grant per user+content), so callers can call it fire-and-forget on every
 * Share click without worrying about double-counting.
 */
import { EMPTY_BALANCE } from './constants.js';
export function createCreditsService(supabase) {
    /**
     * Attempt to grant credits. Returns the grant id, or null if:
     *   - user not authenticated
     *   - idempotent skip (share_bonus already claimed for this content)
     *   - any DB error (swallowed — rewards are best-effort; never break UX)
     */
    async function grantCredits(reason, delta, options = {}) {
        if (delta === 0)
            return null;
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user?.id)
            return null;
        try {
            const { data, error } = await supabase.rpc('grant_credits', {
                p_reason: reason,
                p_delta: delta,
                p_content_type: options.contentType ?? null,
                p_content_id: options.contentId ?? null,
                p_metadata: options.metadata ?? {},
            });
            if (error)
                return null;
            return data ?? null;
        }
        catch {
            return null;
        }
    }
    /**
     * Atomically deduct credits for a concrete action. The DB RPC uses a
     * conditional UPDATE that either fully succeeds or raises
     * `insufficient_credits` (PostgreSQL error code P0002) so races between
     * tabs / clients can't overdraft the balance.
     *
     * Call site pattern:
     *   const result = await spendCredits('unlock', SPEND_COST.IMAGE_UNLOCK_PRO, {
     *     contentType: 'image_model',
     *     contentId: modelId,
     *   });
     *   if (result.status === 'ok') { generate() }
     *   else if (result.status === 'insufficient') { showUpgradePrompt() }
     */
    async function spendCredits(reason, cost, options = {}) {
        if (cost <= 0)
            return { status: 'error', message: 'invalid cost' };
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user?.id)
            return { status: 'error', message: 'not_authenticated' };
        try {
            const { data, error } = await supabase.rpc('spend_credits', {
                p_reason: reason,
                p_cost: cost,
                p_content_type: options.contentType ?? null,
                p_content_id: options.contentId ?? null,
                p_metadata: options.metadata ?? {},
            });
            if (error) {
                // P0002 = insufficient_credits (raised by the RPC)
                if (error.code === 'P0002' || /insufficient_credits/i.test(error.message ?? '')) {
                    return { status: 'insufficient' };
                }
                return { status: 'error', message: error.message ?? 'spend_failed' };
            }
            return { status: 'ok', grantId: data ?? '' };
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'spend_failed';
            return { status: 'error', message };
        }
    }
    /** Fetch the current user's balance. Returns EMPTY_BALANCE for anon / errors. */
    async function getUserBalance() {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user?.id)
            return EMPTY_BALANCE;
        try {
            const { data, error } = await supabase.rpc('get_user_balance', {});
            if (error || !data || !Array.isArray(data) || data.length === 0)
                return EMPTY_BALANCE;
            const row = data[0];
            return {
                creditsAvailable: row.credits_available ?? 0,
                totalEarned: row.total_earned ?? 0,
                totalSpent: row.total_spent ?? 0,
            };
        }
        catch {
            return EMPTY_BALANCE;
        }
    }
    return { grantCredits, spendCredits, getUserBalance };
}
